;

import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import {
  Search,
  X,
  ChevronRight,
  ChevronLeft,
  Eye,
  Users,
  CheckSquare,
  Download,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { auth, db } from "../../firebase";
import InfoModal from "../../components/InfoModal";
import { useAuth } from "../../context/AuthContext";
import { getMessaging, getToken } from "firebase/messaging";
import featureGatingService from "../../services/featureGatingService";
import CaseLimitDialog from "../../components/CaseLimitDialog";
import toast from "react-hot-toast";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { dcPost } from "../../services/districtCourtApi";
import { STATUS_PILL_BASE, statusPillSoft } from "../../styles/statusPill";

const DC_LIST_STATE_PREFIX = "dc_caseList_viewState:";

function DistrictCourtListScreen() {
  const messaging = getMessaging();
  const location = useLocation();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [selectedCases, setSelectedCases] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    total: 0,
    exported: 0,
    currentCase: null,
    startTime: null,
    failedCases: []
  });
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [exportSummary, setExportSummary] = useState({ eventsCreated: 0, casesWithEvents: 0 });
  const [error, setError] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const { globalUser } = useAuth();
  const userId = globalUser?.uid;

  const [duplicateCases, setDuplicateCases] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Case limit states
  const [showCaseLimitDialog, setShowCaseLimitDialog] = useState(false);
  const [caseLimitData, setCaseLimitData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: ''
  });

  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [loadingCaseKey, setLoadingCaseKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage] = useState(10);
  const [viewStateRestored, setViewStateRestored] = useState(false);

  // Get session and search params from navigation state
  const sessionId = location.state?.sessionId || "";
  const navStateCode = location.state?.stateCode || "";
  const navDistCode = location.state?.distCode || "";
  const navComplexCode = location.state?.complexCode || "";
  const navSearchBy = location.state?.searchBy || "CSpartyName";

  const searchStatus = location.state?.searchParams?.status || "Unknown";

  useEffect(() => {
    if (location.state && location.state.data) {
      // Support both parsedCases (old format) and direct array
      const cases = location.state.data.parsedCases || location.state.data.cases || location.state.data;
      if (Array.isArray(cases)) {
        setCaseData(cases);
      } else if (cases?.parsedCases) {
        setCaseData(cases.parsedCases);
      }
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setError("");
      try {
        const q = query(
          collection(db, "pending"),
          where("owner", "==", userId)
        );
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.map((doc) => doc.data().caseNo);

        const userQuery = query(
          collection(db, "lawyers"),
          where("uid", "==", userId)
        );
        const userDocSnapshot = await getDocs(userQuery);
        if (!userDocSnapshot.empty) {
          const userData = userDocSnapshot.docs[0].data();
          setUserMobile(userData.mobileWithCountryCode || "");
        } else {
          console.warn("User document not found for UID:", userId);
          setUserMobile("");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load required data");
      }
    };
    fetchData();
  }, [userId]);

  const cases = caseData || [];

  // Stable per-row key so selection survives filtering
  const getCaseKey = (caseItem) =>
    caseItem?.cino ||
    caseItem?.viewDetails?.cino ||
    `${caseItem?.caseTypeNumberYear || ""}__${caseItem?.caseNo || ""}__${caseItem?.viewDetails?.caseNo || ""}`;

  // Client-side filter on petitioner/respondent string, case number, cino, case type
  const filteredCases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      const caseTypePrefix = c?.caseTypeNumberYear
        ? c.caseTypeNumberYear.split("/")[0].trim()
        : "";
      const fields = [
        c?.caseTypeNumberYear,
        c?.petitionerRespondent,
        c?.cino,
        c?.viewDetails?.cino,
        c?.viewDetails?.caseNumberOnly,
        caseTypePrefix,
      ];
      return fields.some(
        (f) => typeof f === "string" && f.toLowerCase().includes(q)
      );
    });
  }, [cases, searchQuery]);

  // Restore persisted view state (page + search) for this navigation entry
  const stateStorageKey = `${DC_LIST_STATE_PREFIX}${location.key || "default"}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(stateStorageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          if (typeof saved.searchQuery === "string") setSearchQuery(saved.searchQuery);
          if (typeof saved.currentPage === "number" && saved.currentPage > 0) {
            setCurrentPage(saved.currentPage);
          }
        }
      }
    } catch (e) {
      // ignore corrupt state
    }
    setViewStateRestored(true);
  }, [stateStorageKey]);

  useEffect(() => {
    if (!viewStateRestored) return;
    try {
      sessionStorage.setItem(
        stateStorageKey,
        JSON.stringify({ currentPage, searchQuery })
      );
    } catch (e) {
      // ignore quota errors
    }
  }, [currentPage, searchQuery, viewStateRestored, stateStorageKey]);

  // Pagination calculations
  const indexOfLastCase = currentPage * casesPerPage;
  const indexOfFirstCase = indexOfLastCase - casesPerPage;
  const currentCases = filteredCases.slice(indexOfFirstCase, indexOfLastCase);
  const totalPages = Math.max(1, Math.ceil(filteredCases.length / casesPerPage));

  // Snap page back if filter shrinks the list
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Pagination handlers — do NOT clear selections on page change (selections are
  // keyed by case identity, not row index, so they're safe across pages/filters)
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const extractParties = (petitionerRespondent) => {
    if (!petitionerRespondent) return { petitioner: "N/A", respondent: "N/A" };
    const parts = petitionerRespondent.split(" | Versus | ");
    if (parts.length >= 2) {
      return { petitioner: parts[0].trim(), respondent: parts[1].trim() };
    } else {
      return { petitioner: petitionerRespondent, respondent: "N/A" };
    }
  };

  const handleCaseSelect = (caseItem) => {
    const key = getCaseKey(caseItem);
    const newSelectedCases = new Set(selectedCases);
    if (newSelectedCases.has(key)) {
      newSelectedCases.delete(key);
    } else {
      newSelectedCases.add(key);
    }
    setSelectedCases(newSelectedCases);
    setSelectAll(
      filteredCases.length > 0 &&
        filteredCases.every((c) => newSelectedCases.has(getCaseKey(c)))
    );
  };

  const handleSelectAll = () => {
    const newSelectedCases = new Set(selectedCases);
    if (selectAll) {
      filteredCases.forEach((c) => newSelectedCases.delete(getCaseKey(c)));
      setSelectedCases(newSelectedCases);
      setSelectAll(false);
    } else {
      filteredCases.forEach((c) => newSelectedCases.add(getCaseKey(c)));
      setSelectedCases(newSelectedCases);
      setSelectAll(true);
    }
  };

  // Keep "Select All" checkbox in sync as the filter changes
  useEffect(() => {
    if (filteredCases.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(filteredCases.every((c) => selectedCases.has(getCaseKey(c))));
  }, [filteredCases, selectedCases]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const monthNames = {
      january: "01", february: "02", march: "03", april: "04",
      may: "05", june: "06", july: "07", august: "08",
      september: "09", october: "10", november: "11", december: "12",
    };
    // "dd-mm-yyyy"
    const hyphenMatch = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (hyphenMatch) {
      return `${hyphenMatch[1]}/${hyphenMatch[2]}/${hyphenMatch[3]}`;
    }
    // "dd-Month-yyyy"
    const dashedMatch = dateStr.match(
      /^(\d{1,2})-(January|February|March|April|May|June|July|August|September|October|November|December)-(\d{4})$/i
    );
    if (dashedMatch) {
      const day = dashedMatch[1].padStart(2, "0");
      const month = monthNames[dashedMatch[2].toLowerCase()];
      return `${day}/${month}/${dashedMatch[3]}`;
    }
    // "2nd April 2026", "13th March 2024", "25th January 2024"
    const naturalMatch = dateStr.match(
      /^(\d{1,2})(?:st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})$/i
    );
    if (naturalMatch) {
      const day = naturalMatch[1].padStart(2, "0");
      const month = monthNames[naturalMatch[2].toLowerCase()];
      return `${day}/${month}/${naturalMatch[3]}`;
    }
    return dateStr;
  };

  const extractPetitionerAdvocates = (petitionersArray) => {
    const advocates = new Set();
    if (Array.isArray(petitionersArray)) {
      petitionersArray.forEach(petitioner => {
        if (petitioner.advocate && petitioner.advocate.trim() !== '') {
          advocates.add(petitioner.advocate.trim());
        }
      });
    }
    return Array.from(advocates);
  };

  const extractRespondentAdvocates = (respondentsArray) => {
    const advocates = new Set();
    if (Array.isArray(respondentsArray)) {
      respondentsArray.forEach(respondent => {
        if (respondent.advocate && respondent.advocate.trim() !== '') {
          advocates.add(respondent.advocate.trim());
        }
      });
    }
    return Array.from(advocates);
  };

  // Fetch case details using the new API
  const fetchCaseDetail = async (caseItem) => {
    const vd = caseItem.viewDetails || {};
    const cino = caseItem.cino || vd.cino || "";
    const caseNo = caseItem.caseNo || vd.caseNo || "";
    const courtCode = caseItem.courtCode || vd.courtCode || caseItem.court_code || "";

    const detailsPayload = {
      sessionId,
      caseNo,
      cino,
      courtCode,
      hideparty: vd.hideparty || "",
      searchFlag: vd.searchFlag || "CScaseNumber",
      stateCode: vd.stateCode || navStateCode,
      distCode: vd.distCode || navDistCode,
      complexCode: vd.complexCode || navComplexCode,
      searchBy: vd.searchBy || navSearchBy,
    };

    // Store case details payload in localStorage
    localStorage.setItem('dc_case_details_payload', JSON.stringify(detailsPayload));

    const detailEndpoint = "/api/common/case-detail";

    const response = await dcPost(detailEndpoint, detailsPayload);
    const data = response.body;

    if (!response.ok || !data?.success) {
      const errorMsg = data?.details || data?.error || "";
      if (response.status === 500 || errorMsg.includes("timeout")) {
        throw new Error("Government server is currently down. Please try again in some time.");
      }
      throw new Error(errorMsg || "Failed to fetch case details");
    }

    // Unwrap: API returns { success, result: { case_details, case_status, ... } }
    const r = data.result || data;

    // If the result already has flat fields (e.g. registration_number at top), return as-is
    if (r.registration_number || r.filing_number) return r;

    // Otherwise normalize the nested API structure into the flat format the app expects
    const cd = r.case_details || {};
    const cs = r.case_status || {};

    // Parse petitioner/respondent entries like "1) Name\nAdvocate- Adv Name"
    const parseEntries = (entries) =>
      (entries || []).map((entry) => {
        const cleaned = entry.replace(/^\d+\)\s*/, "").trim();
        const parts = cleaned.split(/\n|<br\s*\/?>/i);
        const name = (parts[0] || "").trim();
        const advMatch = (parts[1] || "").match(/Advocate[:\-\s]*(.*)/i);
        return { name, advocate: advMatch ? advMatch[1].trim() : "" };
      });

    return {
      // case_details
      case_type: cd.case_type || "",
      filing_number: cd.filing_number || "",
      filing_date: cd.filing_date || "",
      registration_number: cd.registration_number || "",
      registration_date: cd.registration_date || "",
      cnr_number: (cd.cnr_number || "").replace(/\s*\(Note.*\)/, "").trim(),
      // case_status
      first_hearing_date: cs.first_hearing_date || "",
      next_hearing_date: cs.next_hearing_date || "",
      case_status: cs.case_status || cs.disposal_flag || (cs.next_hearing_date ? "Pending" : "Disposed"),
      stage_of_case: cs.case_stage || "",
      court_number_and_judge: cs.court_number_and_judge || "",
      nature_of_disposal: cs.nature_of_disposal || "",
      decision_date: cs.decision_date || "",
      // parties
      petitioners_and_advocates: parseEntries(r.petitioner_and_advocate?.entries),
      respondents_and_advocates: parseEntries(r.respondent_and_advocate?.entries),
      // acts — map under_act → under_act_s for compatibility
      acts_and_sections: (r.acts || []).map((a) => ({
        under_act_s: a.under_act || a.under_act_s || "",
        under_section_s: a.under_section || a.under_section_s || "",
      })),
      // fir details
      fir_details: r.fir_details || [],
      // case history
      history_of_case_hearing: r.case_history || [],
      // ia status
      ia_status: r.ia_status || [],
      // orders — keep separate for UI
      orders: [...(r.interim_orders || []), ...(r.final_orders || [])],
      interim_orders: r.interim_orders || [],
      final_orders: r.final_orders || [],
      // transfer & connected
      case_transfer_details: r.case_transfer_details || [],
      connected_cases: r.connected_cases || [],
      // process details
      process_details: r.process_details || [],
      // court header
      court_name: r.court_header?.court_name || "",
    };
  };

  const handleCaseClick = async (caseItem) => {
    if (loadingCaseKey) return; // prevent overlapping clicks
    setError(null);
    setFetchingDetails(true);
    setLoadingCaseKey(getCaseKey(caseItem));
    try {
      const caseDetails = await fetchCaseDetail(caseItem);
      const searchParams = location.state?.searchParams || {};

      navigate("/portal/districtCourtCaseDetails", {
        state: {
          caseDetails,
          stateCode: navStateCode,
          distCode: navDistCode,
          complexCode: navComplexCode,
          sessionId,
          searchBy: navSearchBy,
          status: searchParams.status?.toLowerCase() || "unknown",
          petresName: extractParties(caseItem.petitionerRespondent).petitioner,
          cino: caseItem.cino || caseItem.viewDetails?.cino,
          caseNumber: caseItem.caseTypeNumberYear,
        },
      });
    } catch (error) {
      console.error("Fetch Case Details Error:", error);
      toast.error(error.message || "Government server is currently down. Please try again in some time.");
    } finally {
      setFetchingDetails(false);
      setLoadingCaseKey(null);
    }
  };

  const exportSelectedCases = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("User not authenticated");
        return;
      }

      const selectedKeys = new Set(selectedCases || []);
      const selectedCaseItems = cases.filter((c) => selectedKeys.has(getCaseKey(c)));

      if (selectedCaseItems.length === 0) {
        alert("No cases selected to export");
        return;
      }

      // Check case limit before proceeding
      const result = await featureGatingService.checkCaseLimitBeforeExport(
        globalUser.uid,
        selectedCaseItems.length,
        "pending"
      );

      if (!result.canProceed) {
        setCaseLimitData({
          currentCount: result.currentCount,
          maxLimit: result.maxLimit,
          planName: result.planName
        });
        setShowCaseLimitDialog(true);
        return;
      }

      setExporting(true);
      setExportProgress({
        total: selectedCaseItems.length,
        exported: 0,
        currentCase: "Preparing to export cases...",
        startTime: Date.now(),
        failedCases: []
      });

      await new Promise(resolve => setTimeout(resolve, 100));

      const duplicatesFound = [];
      let successfulExports = 0;
      let failedExports = 0;
      const failedCasesList = [];
      let totalEventsCreated = 0;
      let casesWithEvents = 0;

      const pendingQuery = query(
        collection(db, "pending"),
        where("owner", "==", user.uid)
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      let currentPendingCount = pendingSnapshot.size;

      for (const caseItem of selectedCaseItems) {
        try {
          if (!caseItem) {
            failedExports++;
            setExportProgress(prev => ({
              ...prev,
              exported: prev.exported + 1,
              failedCases: [...prev.failedCases, 'Unknown'],
              currentCase: `Failed: Case not found`
            }));
            continue;
          }

          setExportProgress(prev => ({
            ...prev,
            currentCase: `Checking case: ${caseItem.caseTypeNumberYear}`,
            exported: prev.exported
          }));

          await new Promise(resolve => setTimeout(resolve, 50));

          // Fetch case details using new API
          let caseDetails;
          try {
            caseDetails = await fetchCaseDetail(caseItem);
          } catch (fetchErr) {
            console.warn(`Failed to fetch details for case: ${caseItem.caseTypeNumberYear} - ${fetchErr.message}`);
            failedExports++;
            failedCasesList.push(caseItem.caseTypeNumberYear);
            toast.error(`Failed for case: ${caseItem.caseTypeNumberYear}. Skipping...`);
            setExportProgress(prev => ({
              ...prev,
              exported: prev.exported + 1,
              failedCases: [...prev.failedCases, caseItem.caseTypeNumberYear],
              currentCase: `Failed: ${caseItem.caseTypeNumberYear}`
            }));
            continue;
          }

          if (!caseDetails) {
            failedExports++;
            failedCasesList.push(caseItem.caseTypeNumberYear);
            setExportProgress(prev => ({
              ...prev,
              exported: prev.exported + 1,
              failedCases: [...prev.failedCases, caseItem.caseTypeNumberYear],
              currentCase: `Failed: ${caseItem.caseTypeNumberYear}`
            }));
            continue;
          }

          const caseNo = caseDetails.registration_number || caseItem.caseTypeNumberYear;

          // Check for duplicate
          let isDuplicate = false;
          const dupPendingQuery = query(
            collection(db, "pending"),
            where("caseNo", "==", caseNo),
            where("owner", "==", user.uid)
          );
          const dupDisposedQuery = query(
            collection(db, "disposed"),
            where("caseNo", "==", caseNo),
            where("owner", "==", user.uid)
          );
          const [dupPendingSnap, dupDisposedSnap] = await Promise.all([
            getDocs(dupPendingQuery),
            getDocs(dupDisposedQuery)
          ]);

          if (!dupPendingSnap.empty || !dupDisposedSnap.empty) {
            duplicatesFound.push({
              caseNo: caseNo,
              collection: !dupPendingSnap.empty ? "pending" : "disposed"
            });
            isDuplicate = true;
            setExportProgress(prev => ({
              ...prev,
              exported: prev.exported + 1,
              currentCase: `Skipped duplicate: ${caseItem.caseTypeNumberYear}`
            }));
          }

          if (!isDuplicate) {
            setExportProgress(prev => ({
              ...prev,
              currentCase: `Exporting: ${caseItem.caseTypeNumberYear}`
            }));

            const courtName = caseDetails.court_number_and_judge || "District Court";
            const caseStatus =
              (caseDetails.case_status || "").toLowerCase().includes("pending")
                ? "pending"
                : "disposed";
            const caseFiledDate = formatDate(caseDetails.filing_date || "");
            const nextHearingDate = formatDate(caseDetails.next_hearing_date || "");
            const caseHistoryRaw = caseDetails.history_of_case_hearing || caseDetails.case_history || [];
            const previousHearingDate = formatDate(
              caseHistoryRaw[caseHistoryRaw.length - 1]?.business_on_date || ""
            );

            const caseHistoryForExport = caseHistoryRaw.map((history) => ({
              registration_number: history.registration_number || "",
              judge: history.judge || "",
              business_on_date: formatDate(history.business_on_date),
              hearing_date: formatDate(history.hearing_date),
              purpose_of_hearing: history.purpose_of_hearing || "",
            }));

            const petitionersArray = caseDetails.petitioners_and_advocates || caseDetails.petitioners || [];
            const respondentsArray = caseDetails.respondents_and_advocates || caseDetails.respondents || [];
            const petitionerName = petitionersArray[0]?.name || "N/A";
            const respondentName = respondentsArray[0]?.name || "N/A";

            const allPetitionerAdvocates = extractPetitionerAdvocates(petitionersArray);
            const allRespondentAdvocates = extractRespondentAdvocates(respondentsArray);

            const searchPayloadStr = localStorage.getItem('dc_search_payload');
            const vd = caseItem.viewDetails || {};
            const caseDetailParams = {
              caseNo: caseItem.caseNo || vd.caseNo || "",
              cino: caseItem.cino || vd.cino || "",
              courtCode: caseItem.courtCode || vd.courtCode || "",
              hideparty: vd.hideparty || "",
              searchFlag: vd.searchFlag || "CScaseNumber",
              stateCode: vd.stateCode || navStateCode,
              distCode: vd.distCode || navDistCode,
              complexCode: vd.complexCode || navComplexCode,
              searchBy: vd.searchBy || navSearchBy,
            };
            const combinedPayload = {
              searchPayload: searchPayloadStr ? JSON.parse(searchPayloadStr) : null,
              detailsPayload: {
                sessionId,
                ...caseDetailParams,
              },
              exportedAt: new Date().toISOString(),
              caseexportedfrom: "DistrictCourt",
            };

            let subscriptionMetadata = null;
            if (caseStatus === "pending") {
              subscriptionMetadata = await featureGatingService.getSubscriptionMetadataForNewCase(
                user.uid,
                currentPendingCount
              );
            }

            const caseDataToExport = {
              caseNo: caseDetails.registration_number || caseItem.caseTypeNumberYear,
              filingNumber: caseDetails.filing_number || "",
              caseType:
                caseDetails.case_type ||
                caseItem.caseTypeNumberYear.split("/")[0].trim(),
              caseFiledDate,
              nextHearingDate,
              previousHearingDate,
              courtName,
              petitionerName,
              petitionerAdvocate: petitionersArray[0]?.advocate || "",
              respondentName,
              respondentAdvocate: respondentsArray[0]?.advocate || "",
              allPetitionerAdvocates,
              allRespondentAdvocates,
              partyContactNo: "",
              appearingFor: "",
              team: [userId],
              files: [],
              casenotes: "",
              status: caseStatus,
              owner: user.uid,
              firDetails: Array.isArray(caseDetails.fir_details) && caseDetails.fir_details.length > 0
                ? {
                    policeStation: caseDetails.fir_details[0]?.police_station || "",
                    firNumber: caseDetails.fir_details[0]?.fir_number || "",
                    year: caseDetails.fir_details[0]?.year || "",
                  }
                : {
                    policeStation: caseDetails.fir_details?.police_station || "",
                    firNumber: caseDetails.fir_details?.fir_number || "",
                    year: caseDetails.fir_details?.year || "",
                  },
              acts: caseDetails.acts_and_sections?.length > 0
                ? {
                    underAct: caseDetails.acts_and_sections[0]?.under_act_s || "",
                    underSection: caseDetails.acts_and_sections[0]?.under_section_s || "",
                  }
                : {
                    underAct: caseDetails.acts?.under_act_s || "",
                    underSection: caseDetails.acts?.under_section_s || "",
                  },
              allPetitioners: petitionersArray,
              allRespondents: respondentsArray,
              allActs: caseDetails.acts_and_sections || [],
              iaStatus: caseDetails.ia_status || [],
              caseTransferDetails: caseDetails.case_transfer_details || [],
              orders: caseDetails.orders || [],
              interimOrders: caseDetails.interim_orders || [],
              finalOrders: caseDetails.final_orders || [],
              connectedCases: caseDetails.connected_cases || [],
              processDetails: caseDetails.process_details || [],
              caseHistory: caseHistoryForExport,
              cnrNumber: caseDetails.cnr_number || "",
              decisionDate: formatDate(caseDetails.decision_date || ""),
              natureOfDisposal: caseDetails.nature_of_disposal || "",
              firstHearingDate: formatDate(caseDetails.first_hearing_date || ""),
              stageOfCase: caseDetails.stage_of_case || "",
              registrationDate: formatDate(caseDetails.registration_date || ""),
              params: caseDetailParams,
              payload: combinedPayload,
              ...(subscriptionMetadata && { subscriptionMetadata }),
              timestamp: serverTimestamp(),
            };

            // Check case limit before adding
            const limitResult = await featureGatingService.checkCaseLimitBeforeExport(
              user.uid,
              1,
              caseStatus
            );

            if (!limitResult.canProceed) {
              setCaseLimitData({
                currentCount: limitResult.currentCount,
                maxLimit: limitResult.maxLimit,
                planName: limitResult.planName
              });
              setShowCaseLimitDialog(true);
              break;
            }

            const caseRef = await addDoc(
              collection(db, caseStatus),
              caseDataToExport
            );
            console.log("Case added to Firestore:", caseRef.id);

            let fcmToken = "";
            try {
              fcmToken = await getToken(messaging, {
                vapidKey:
                  "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38",
              });
            } catch (err) {
              console.warn("Could not get FCM token:", err.message);
            }

            // Event and Reminder Creation
            if (nextHearingDate) {
              try {
                const eventDateStr = nextHearingDate.split("/").reverse().join("-");
                const eventDate = new Date(eventDateStr);
                if (isNaN(eventDate.getTime())) {
                  console.warn("Invalid hearing date format:", nextHearingDate);
                } else {
                  const reminderDate = new Date(eventDate);
                  reminderDate.setHours(8, 0, 0, 0);

                  const eventTimeIST = new Date(eventDate);
                  eventTimeIST.setHours(18, 0, 0, 0);

                  const now = new Date();
                  let eventsCreatedForThisCase = 0;

                  if (reminderDate > now) {
                    eventsCreatedForThisCase++;
                    const reminderEventData = {
                      "Case No": caseDetails.registration_number || caseItem.caseTypeNumberYear,
                      date: reminderDate.toISOString().split("T")[0],
                      fcmToken: fcmToken || null,
                      mobile: userMobile || null,
                      time: "08:00 AM",
                      time1: reminderDate.getTime(),
                      title: `${petitionerName} VS ${respondentName}`,
                      description: `Hearing Reminder : Case No. - ${caseDetails.registration_number || caseItem.caseTypeNumberYear}`,
                      isScheduled: reminderDate > now,
                      status: caseStatus,
                      eventType: "reminder",
                    };
                    const reminderEventRef = await addDoc(
                      collection(db, "lawyers", user.uid, "events"),
                      reminderEventData
                    );
                    const reminderDoc = {
                      eventId: reminderEventRef.id,
                      reminderTime: reminderDate,
                      eventTitle: `Hearing Reminder : Case No. - ${caseDetails.registration_number || caseItem.caseTypeNumberYear}`,
                      caseNo: caseDetails.registration_number || caseItem.caseTypeNumberYear,
                      status: "pending",
                      createdAt: new Date(),
                      scheduledBy: user.uid,
                      recipientId: user.uid,
                    };
                    await addDoc(collection(db, "eventReminders"), reminderDoc);
                  }

                  if (eventTimeIST > now) {
                    eventsCreatedForThisCase++;
                    const mainEventData = {
                      "Case No": caseDetails.registration_number || caseItem.caseTypeNumberYear,
                      date: eventDate.toISOString().split("T")[0],
                      fcmToken: fcmToken || null,
                      mobile: userMobile || null,
                      time: "06:00 PM",
                      time1: eventTimeIST.getTime(),
                      title: `Update Next Hearing: ${petitionerName} VS ${respondentName}`,
                      description: `Case No. - ${caseDetails.registration_number || caseItem.caseTypeNumberYear}`,
                      isScheduled: eventTimeIST > now,
                      status: caseStatus,
                      eventType: "updateReminder",
                      isHidden: true,
                    };
                    const mainEventRef = await addDoc(
                      collection(db, "lawyers", user.uid, "events"),
                      mainEventData
                    );
                    const mainReminderDoc = {
                      eventId: mainEventRef.id,
                      reminderTime: eventTimeIST,
                      eventTitle: `Update Next Hearing : ${caseDetails.registration_number || caseItem.caseTypeNumberYear}`,
                      caseNo: caseDetails.registration_number || caseItem.caseTypeNumberYear,
                      status: "pending",
                      createdAt: new Date(),
                      scheduledBy: user.uid,
                      recipientId: user.uid,
                    };
                    await addDoc(collection(db, "eventReminders"), mainReminderDoc);
                  }

                  if (eventsCreatedForThisCase > 0) {
                    totalEventsCreated += eventsCreatedForThisCase;
                    casesWithEvents++;
                  }
                }
              } catch (notificationError) {
                console.error("Notification setup error:", notificationError);
              }
            }

            successfulExports++;

            if (caseStatus === "pending") {
              currentPendingCount++;
            }

            setExportProgress((prev) => ({
              ...prev,
              exported: prev.exported + 1,
              currentCase: `Completed: ${caseItem.caseTypeNumberYear}`
            }));

            await new Promise(resolve => setTimeout(resolve, 100));
          }

        } catch (caseError) {
          console.error(`Error processing case ${caseItem?.caseTypeNumberYear}:`, caseError);
          failedExports++;
          failedCasesList.push(caseItem?.caseTypeNumberYear || 'Unknown');
          toast.error(`Failed for case: ${caseItem?.caseTypeNumberYear || 'Unknown'}. Skipping...`);

          setExportProgress((prev) => ({
            ...prev,
            exported: prev.exported + 1,
            failedCases: [...prev.failedCases, caseItem?.caseTypeNumberYear || 'Unknown'],
            currentCase: `Failed: ${caseItem?.caseTypeNumberYear || 'Unknown'}`
          }));

          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (duplicatesFound.length === selectedCaseItems.length) {
        setExporting(false);
        setDuplicateCases(duplicatesFound);
        setShowDuplicateModal(true);
        setExportProgress({
          total: 0,
          exported: 0,
          currentCase: null,
          startTime: null,
          failedCases: []
        });
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      setExportSummary({
        eventsCreated: totalEventsCreated,
        casesWithEvents: casesWithEvents
      });

      if (successfulExports > 0 || duplicatesFound.length > 0) {
        if (duplicatesFound.length > 0 && successfulExports === 0) {
          setDuplicateCases(duplicatesFound);
          setShowDuplicateModal(true);
        } else {
          setIsInfoModalOpen(true);
        }
      } else if (failedExports > 0) {
        alert(`All ${failedExports} cases failed to export. Please check the console for errors.`);
      }

    } catch (error) {
      console.error("Export error:", error);
      alert(
        `Error exporting cases: ${error.message || "Network or server error"}`
      );
    } finally {
      setExporting(false);
      setExportProgress({
        total: 0,
        exported: 0,
        currentCase: null,
        startTime: null,
        failedCases: []
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="mb-4 flex items-center gap-1.5 text-sm">
          <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link to="/portal/fetchfromdistrictcourt" className="text-slate-500 hover:text-slate-800">District Court</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Results</span>
        </div>
        <div className="mb-4"><FindCaseStepper current={3} /></div>

        {/* Summary stat cards — only counts derivable from the list records */}
        {cases.length > 0 && (
          <div className="mb-4 grid grid-cols-2 gap-4">
            {[
              { label: "Total Found", value: cases.length, Icon: Users, tint: "bg-indigo-50 text-indigo-500" },
              { label: "Selected", value: selectedCases.size, Icon: CheckSquare, tint: "bg-blue-50 text-blue-500" },
            ].map(({ label, value, Icon, tint }) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold leading-tight text-slate-900">{value}</p>
                  <p className="truncate text-xs text-slate-500">{label}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading Overlay when fetching case details */}
        {fetchingDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
              <div className="flex justify-center mb-4">
                <svg className="animate-spin h-10 w-10 text-yellow-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-slate-600 text-sm animate-pulse">
                Please wait, magic will happen soon...
              </p>
            </div>
          </div>
        )}

        {/* Enhanced Export Progress Modal */}
        {exporting && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300 scale-100">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Exporting Your Cases
                </h3>

                <p className="text-sm text-gray-600 mb-6">
                  Please wait while we process and export your selected cases...
                </p>

                <div className="relative mb-6">
                  <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out relative"
                      style={{
                        width: exportProgress.total > 0
                          ? `${(exportProgress.exported / exportProgress.total) * 100}%`
                          : '0%'
                      }}
                    >
                      <div className="absolute inset-0 bg-white bg-opacity-20 animate-pulse"></div>
                      <div
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
                          animation: 'slide 1s linear infinite'
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-700">
                      {exportProgress.total > 0
                        ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%`
                        : '0%'}
                    </span>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-600">Cases Exported:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {exportProgress.exported} / {exportProgress.total}
                    </span>
                  </div>

                  {exportProgress.exported > 0 && exportProgress.startTime && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Estimated Time:</span>
                      <span className="text-sm font-semibold text-gray-700">
                        {(() => {
                          const elapsed = Date.now() - exportProgress.startTime;
                          const avgTimePerCase = elapsed / exportProgress.exported;
                          const remaining = exportProgress.total - exportProgress.exported;
                          const estimatedMs = remaining * avgTimePerCase;
                          const estimatedSeconds = Math.ceil(estimatedMs / 1000);
                          if (estimatedSeconds < 60) return `${estimatedSeconds}s remaining`;
                          return `${Math.ceil(estimatedSeconds / 60)}m remaining`;
                        })()}
                      </span>
                    </div>
                  )}

                  {exportProgress.failedCases.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      Failed: {exportProgress.failedCases.length} case(s)
                    </div>
                  )}
                </div>

                {exportProgress.currentCase && (
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span className="text-sm text-gray-600 truncate max-w-xs">
                      {exportProgress.currentCase}
                    </span>
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800 flex items-start">
                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Please do not close this window or navigate away until the export is complete.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slide {
            0% { transform: translateX(0); }
            100% { transform: translateX(20px); }
          }
        `}</style>

        {/* Export Progress Banner */}
        {exporting && (
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-lg mb-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                <div>
                  <span className="font-medium">Exporting cases...</span>
                  <span className="ml-2 text-sm opacity-90">
                    {exportProgress.exported}/{exportProgress.total} completed
                  </span>
                </div>
              </div>
              <div className="text-lg font-bold">
                {exportProgress.total > 0
                  ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%`
                  : '0%'}
              </div>
            </div>
          </div>
        )}

        {/* Cases Table */}
        <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {cases.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-base font-medium text-slate-700">No cases found</p>
              <p className="mt-1 text-sm text-slate-400">Try adjusting your search criteria</p>
            </div>
          ) : (
            <>
              {/* Toolbar — search + export */}
              <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search in results..."
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                      title="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  {selectedCases.size > 0 && (
                    <span className="text-sm text-slate-500">{selectedCases.size} selected</span>
                  )}
                  <button
                    onClick={exportSelectedCases}
                    className={`flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold transition-colors ${
                      exporting || selectedCases.size === 0
                        ? "cursor-not-allowed bg-slate-100 text-slate-400"
                        : "bg-yellow-500 text-slate-900 hover:bg-yellow-400"
                    }`}
                    disabled={exporting || selectedCases.size === 0}
                  >
                    {exporting ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Exporting ({exportProgress.exported}/{exportProgress.total})
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Export Selected ({selectedCases.size})
                      </>
                    )}
                  </button>
                </div>
              </div>

              {filteredCases.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-base font-medium text-slate-700">No cases match your search</p>
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="mt-2 text-sm font-medium text-yellow-600 hover:underline"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="w-12 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                            disabled={filteredCases.length === 0}
                            title="Select all"
                          />
                        </th>
                        <th className="px-4 py-3">Case Number</th>
                        <th className="px-4 py-3">Petitioner/Respondent</th>
                        <th className="px-4 py-3">Case Type</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">CNR</th>
                        <th className="w-16 px-4 py-3 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentCases.map((caseItem) => {
                        const rowKey = getCaseKey(caseItem);
                        const isRowLoading = loadingCaseKey === rowKey;
                        const rowClickable = !loadingCaseKey;
                        const caseType = caseItem.caseTypeNumberYear
                          ? caseItem.caseTypeNumberYear.split("/")[0].trim()
                          : "N/A";
                        const status = searchStatus === "Both" ? "Unknown" : searchStatus;
                        const statusLower = status.toLowerCase();
                        const isPending = statusLower.includes("pending");
                        const isDisposed = statusLower.includes("disposed");
                        const cnr = caseItem.cino || caseItem.viewDetails?.caseNumberOnly;
                        const openCase = () => rowClickable && handleCaseClick(caseItem);
                        const cellClass = `px-4 py-3.5 text-sm text-slate-600 ${
                          rowClickable ? "cursor-pointer" : "cursor-wait"
                        }`;
                        return (
                          <tr
                            key={rowKey}
                            className={`border-b border-slate-100 transition-colors duration-150 ${
                              selectedCases.has(rowKey) ? "bg-yellow-50/60" : "hover:bg-slate-50"
                            } ${loadingCaseKey && !isRowLoading ? "opacity-50" : ""}`}
                          >
                            <td className="px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedCases.has(rowKey)}
                                onChange={() => handleCaseSelect(caseItem)}
                                className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-yellow-500"
                              />
                            </td>
                            <td className={`${cellClass} font-medium text-slate-900`} onClick={openCase}>
                              <div className="flex items-center gap-2">
                                {isRowLoading && (
                                  <svg className="h-4 w-4 animate-spin text-yellow-500" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                )}
                                <span>{caseItem.caseTypeNumberYear}</span>
                              </div>
                            </td>
                            <td className={cellClass} onClick={openCase}>
                              {caseItem.petitionerRespondent || "—"}
                            </td>
                            <td className={cellClass} onClick={openCase}>
                              {caseType}
                            </td>
                            <td className={cellClass} onClick={openCase}>
                              {isPending || isDisposed ? (
                                <span className={`${STATUS_PILL_BASE} ${statusPillSoft(status)}`}>
                                  {status}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className={`${cellClass} text-xs text-slate-500`} onClick={openCase}>
                              {cnr || "—"}
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <button
                                type="button"
                                onClick={openCase}
                                disabled={!rowClickable}
                                title="View case details"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-wait"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer — result range + pagination */}
              {filteredCases.length > 0 && (
                <div className="flex flex-col items-center gap-4 border-t border-slate-100 px-4 py-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-slate-500">
                    Showing {indexOfFirstCase + 1} to {Math.min(indexOfLastCase, filteredCases.length)} of{" "}
                    {filteredCases.length} results
                  </p>

                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>

                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => handlePageChange(1)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="px-1 text-slate-400">...</span>}
                        </>
                      )}

                      {getPageNumbers().map((number) => (
                        <button
                          key={number}
                          onClick={() => handlePageChange(number)}
                          className={`h-9 min-w-[2.25rem] rounded-lg px-2 text-sm font-medium transition-colors ${
                            currentPage === number
                              ? "bg-yellow-500 text-slate-900"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      {currentPage < totalPages - 2 && (
                        <>
                          {currentPage < totalPages - 3 && <span className="px-1 text-slate-400">...</span>}
                          <button
                            onClick={() => handlePageChange(totalPages)}
                            className="h-9 min-w-[2.25rem] rounded-lg border border-slate-200 px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}

                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        title="Information"
        message={`All selected cases exported successfully! ${exportSummary.eventsCreated > 0 ? `\n\n${exportSummary.eventsCreated} events created for ${exportSummary.casesWithEvents} case${exportSummary.casesWithEvents !== 1 ? 's' : ''} with future hearing dates:\n• Reminder at 8:00 AM (one day before next hearing date)\n• Update reminder at 6:00 PM (on next hearing date)` : '\n\nNo events were created (next hearing dates are in the past).'}`}
      />

      <InfoModal
        isOpen={showDuplicateModal}
        onClose={() => {
          setShowDuplicateModal(false);
          setDuplicateCases([]);
        }}
        title="Duplicate Cases Found"
        message={
          duplicateCases.length === 1
            ? `Case ${duplicateCases[0]?.caseNo} is already exported and saved in your profile under ${duplicateCases[0]?.collection} cases.`
            : `${duplicateCases.length} cases are already exported. Cases: ${duplicateCases.map(c => c.caseNo).join(', ')}`
        }
      />

      <CaseLimitDialog
        isOpen={showCaseLimitDialog}
        onClose={() => setShowCaseLimitDialog(false)}
        currentCount={caseLimitData.currentCount}
        maxLimit={caseLimitData.maxLimit}
        planName={caseLimitData.planName}
      />
    </div>
  );
}

export default DistrictCourtListScreen;
